// Client-safe types + pure utilities for the venue-first hotel pages (/venues).
// NO `fs`/`path` here so this module can be imported from "use client" components.
// The server-only reader (`lib/eventDeals.ts`) re-exports these.

import { HotelSummary } from "@/generated/search.openapi";
import { formatCurrency } from "@/lib/utils";

/** One entry of `content/venues/_events.json`, written by the Python generator. */
export type EventManifestEntry = {
  slug: string;
  event_id: string;
  city_slug: string;
  city_name: string;
  name: string;
  venue_name: string | null;
  local_date: string;
  /** ISO calendar date (the morning after the event). Unpublished once it is on or before today. */
  expires_at: string;
  hotel_count: number;
  /** Slug of the parent venue hub. Every published event sits under a hub. */
  venue_slug: string;
  /** The event page's own path: `/venues/{city}/{venue}/{event}`. */
  path: string;
  /** Page path to index for this event (the hub for long runs). */
  canonical_path: string;
};

/** One entry of `content/venues/_venues.json`, written by the Python generator. */
export type VenueManifestEntry = {
  venue_slug: string;
  venue_id: string | null;
  /** The hub's path: `/venues/{city}/{venue}`. */
  path: string;
  city_slug: string;
  city_name: string;
  name: string | null;
  /** Venue photo URL (same as `VenueSnapshot.image.url`), or null. Absent in older manifests. */
  image_url?: string | null;
  event_count: number;
  hotel_count: number;
  next_event_date: string | null;
  /** ISO calendar date: the morning after the hub's last listed event. */
  expires_at: string;
};

/** The hub's photo as the generator resolved it: a Ticketmaster venue image or the next event's hero. */
export type VenueImage = {
  url: string;
  source: "venue" | "event";
  width: number | null;
  height: number | null;
};

export type EventPriceRange = {
  minimum: number | null;
  maximum: number | null;
  currency: string | null;
};

/** Straight-line distance from the venue to a hotel, keyed by vervotech property id. */
export type HotelDistance = { distance_km: number; walk_minutes: number };

export type SiblingListing = {
  name: string;
  url: string;
  local_time: string | null;
};

export type PerformerKind = "person" | "group" | "team" | "unknown";

export type EventPerformer = { name: string; kind: PerformerKind };

/** Ticketmaster facts the Event JSON-LD recommends (GON-7729); every field is nullable or empty. */
export type EventFacts = {
  /** Ticketmaster status code (`onsale`, `offsale`, ...) or null. */
  status: string | null;
  performers: EventPerformer[];
  /** Promoter name, or null. */
  organizer: string | null;
  end_local_date: string | null;
  end_local_time: string | null;
  /** ISO datetime when public sales open, or null (the feed's 1900 sentinel is never stored). */
  sale_start: string | null;
};

export type VenueEventRef = EventFacts & {
  slug: string;
  name: string;
  local_date: string | null;
  local_time: string | null;
  timezone?: string | null;
  url: string;
  price_range: EventPriceRange | null;
};

export type EventVenue = {
  name: string | null;
  address_line_1: string | null;
  city: string | null;
  state_code: string | null;
  country_code: string | null;
  postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
};

/** One `content/venues/{city}/{venue}/{slug}.json` file with hotels decoded via the OpenAPI codec. */
export type EventSnapshot = {
  slug: string;
  event_id: string;
  name: string;
  url: string;
  local_date: string;
  local_time: string | null;
  timezone: string | null;
  venue: EventVenue;
  classification: { segment: string | null; genre: string | null };
  image_url: string | null;
  /** The night's photo (or category art) with dimensions, or null (older content has no key). */
  image: VenueImage | null;
  city_slug: string;
  city_name: string;
  country_code: string;
  description: string;
  radius_km: number;
  checkin_str: string;
  checkout_str: string;
  expires_at: string;
  generated_at: string;
  /** Slug of the parent venue hub. Every published event sits under a hub. */
  venue_slug: string;
  /** This page's own path: `/venues/{city}/{venue}/{event}`. */
  path: string;
  /** The parent hub's path: `/venues/{city}/{venue}`. */
  venue_path: string;
  /** Page path search engines should treat as canonical (the hub for long runs). */
  canonical_path: string;
  price_range: EventPriceRange | null;
  distances: Record<string, HotelDistance>;
  also_at_venue: SiblingListing[];
  other_dates: string[];
  hotels: HotelSummary[];
} & EventFacts & { sale_end: string | null };

/** One `content/venues/{city}/{venue}/index.json` file with hotels decoded via the OpenAPI codec. */
export type VenueSnapshot = {
  venue_slug: string;
  venue_id: string | null;
  /** The hub's path: `/venues/{city}/{venue}`. */
  path: string;
  venue: EventVenue;
  city_slug: string;
  city_name: string;
  country_code: string;
  description: string;
  radius_km: number;
  checkin_window: { start: string; end: string };
  events: VenueEventRef[];
  expires_at: string;
  generated_at: string;
  distances: Record<string, HotelDistance>;
  hotels: HotelSummary[];
  /** Venue photo, or null when none resolved (older content on disk has no key). */
  image: VenueImage | null;
};

type DistanceSource = { distances: Record<string, HotelDistance> };

export const EVENT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Today's calendar date as `YYYY-MM-DD` in UTC. Runs at build time for the static
 * venue and event pages, and per request for the `force-dynamic` legacy
 * `/events/[[...segments]]` redirect route.
 */
export function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * An entry is live while its expiry is strictly after today. Both sides are
 * `YYYY-MM-DD` strings, so a plain string comparison is a correct date comparison
 * and no `Date` object (and no timezone) is involved.
 */
export function isPublishedEntry(
  entry: EventManifestEntry,
  today: string
): boolean {
  if (!ISO_DATE_PATTERN.test(entry.expires_at ?? "")) return false;
  if (!ISO_DATE_PATTERN.test(entry.local_date ?? "")) return false;
  if (!EVENT_SLUG_PATTERN.test(entry.slug ?? "")) return false;
  if (!EVENT_SLUG_PATTERN.test(entry.city_slug ?? "")) return false;
  if (!EVENT_SLUG_PATTERN.test(entry.venue_slug ?? "")) return false;
  return entry.expires_at > today;
}

export const VENUES_ROOT_PATH = "/venues";
export const DEFAULT_OG_IMAGE = "https://www.gondola.ai/assets/meta-bg.png";

/**
 * Only Ticketmaster-hosted https images are rendered. The URL comes from a third-party
 * feed and lands in `<img src>`, Open Graph and JSON-LD, so a malformed or foreign value
 * is dropped rather than becoming an arbitrary resource reference.
 */
export function isTicketmasterImageUrl(url: unknown): url is string {
  if (typeof url !== "string") return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  const host = parsed.hostname.toLowerCase();
  return TICKETMASTER_IMAGE_HOSTS.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`)
  );
}

/** Ticketmaster Discovery hosts: ticketm.net/ticketmaster.com plus the Universe and TicketWeb CDNs. */
const TICKETMASTER_IMAGE_HOSTS: readonly string[] = [
  "ticketm.net",
  "ticketmaster.com",
  "universe.com",
  "ticketweb.com",
];

/** Validate a stored image block; anything outside the exact generator shape is null. */
export function venueImageFrom(value: unknown): VenueImage | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  if (!isTicketmasterImageUrl(raw.url)) return null;
  if (raw.source !== "venue" && raw.source !== "event") return null;
  const dimension = (n: unknown) =>
    typeof n === "number" && Number.isFinite(n) ? n : null;
  return {
    url: raw.url,
    source: raw.source,
    width: dimension(raw.width),
    height: dimension(raw.height),
  };
}

/**
 * Narrowest photo that still reads as a full-width hero or a social card. Mirrors
 * `HERO_MIN_WIDTH` in api_search/scripts/generate_event_snapshots.py. Many Ticketmaster venues
 * only carry a 205x115 photo; those render in a compact side-by-side card instead.
 */
export const HERO_IMAGE_MIN_WIDTH = 640;

/** True when the image is wide enough to stretch across the page (unknown width counts as not). */
export function isHeroImage(
  image: VenueImage | null | undefined
): image is VenueImage {
  return (
    image != null &&
    typeof image.width === "number" &&
    image.width >= HERO_IMAGE_MIN_WIDTH
  );
}

/** The social card image: the hub photo when it is hero-sized, else the site default. */
export function venueOgImage(snapshot: VenueSnapshot): string {
  return isHeroImage(snapshot.image) ? snapshot.image.url : DEFAULT_OG_IMAGE;
}

/** The night page's social card image: its photo when hero-sized, else the site default. */
export function eventOgImage(snapshot: EventSnapshot): string {
  return isHeroImage(snapshot.image) ? snapshot.image.url : DEFAULT_OG_IMAGE;
}

/**
 * Prompt for the Concierge handoff. Plain text: the caller URL-encodes it, the planner
 * prefills it into the composer, and nothing is sent until the user does.
 */
export function conciergePromptFor({
  venueName,
  cityName,
  date,
  eventName,
}: {
  venueName: string | null;
  cityName: string;
  date?: string | null;
  eventName?: string | null;
}): string {
  const where = venueName
    ? `near ${venueName} in ${cityName}`
    : `in ${cityName}`;
  const when = date
    ? eventName
      ? ` for ${eventName} on ${formatEventDateShort(date)}`
      : ` for ${formatEventDateShort(date)}`
    : "";
  return `Find me a hotel ${where}${when}. I'd like to use points.`;
}

export function conciergeHrefFor(prompt: string): string {
  return `/concierge?prompt=${encodeURIComponent(prompt)}`;
}

export function venueCityPath(citySlug: string): string {
  return `${VENUES_ROOT_PATH}/${citySlug}`;
}

export function venuePagePath(citySlug: string, venueSlug: string): string {
  return `${venueCityPath(citySlug)}/${venueSlug}`;
}

export function eventPagePath(
  citySlug: string,
  venueSlug: string,
  eventSlug: string
): string {
  return `${venuePagePath(citySlug, venueSlug)}/${eventSlug}`;
}

/** A hub is live while its expiry (the morning after its last event) is after today. */
export function isPublishedVenue(
  entry: VenueManifestEntry,
  today: string
): boolean {
  if (!ISO_DATE_PATTERN.test(entry.expires_at ?? "")) return false;
  if (!EVENT_SLUG_PATTERN.test(entry.venue_slug ?? "")) return false;
  if (!EVENT_SLUG_PATTERN.test(entry.city_slug ?? "")) return false;
  return entry.expires_at > today;
}

/** The path search engines should index for an event page; falls back to the page itself. */
export function canonicalPathFor(snapshot: EventSnapshot): string {
  return snapshot.canonical_path &&
    snapshot.canonical_path.startsWith(`${VENUES_ROOT_PATH}/`)
    ? snapshot.canonical_path
    : eventPagePath(snapshot.city_slug, snapshot.venue_slug, snapshot.slug);
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * "Friday, November 20, 2026" from a `YYYY-MM-DD` string. The weekday is computed
 * with `Date.UTC` so the calendar date never shifts with the build machine's zone.
 */
export function formatEventDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${WEEKDAY_NAMES[weekday]}, ${MONTH_NAMES[month - 1]} ${day}, ${year}`;
}

/** "November 20, 2026" without the weekday, for titles and FAQ copy. */
export function formatEventDateShort(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return `${MONTH_NAMES[month - 1]} ${day}, ${year}`;
}

/** "Dec 17" from a `YYYY-MM-DD` string. */
export function formatMonthDay(isoDate: string): string {
  const [, month, day] = isoDate.split("-").map(Number);
  return `${MONTH_NAMES[month - 1].slice(0, 3)} ${day}`;
}

/** "7:30 PM" from a `HH:MM:SS` local time string; null when unknown. */
export function formatEventTime(localTime: string | null): string | null {
  if (!localTime) return null;
  const [hourText, minuteText] = localTime.split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText ?? "0");
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

/** "Tickets from $86" from the provider's price range; null when no minimum is listed. */
export function ticketPriceText(price: EventPriceRange | null): string | null {
  if (!price || price.minimum == null) return null;
  return `Tickets from ${formatCurrency(price.minimum, price.currency ?? "USD", 0)}`;
}

const KM_PER_MILE = 1.609344;

/** "0.4 mi · 8 min walk" for a stored distance. */
export function formatDistance(distance: HotelDistance): string {
  const miles = distance.distance_km / KM_PER_MILE;
  return `${miles.toFixed(1)} mi · ${distance.walk_minutes} min walk`;
}

export function hotelDistance(
  source: DistanceSource,
  hotel: HotelSummary
): HotelDistance | null {
  if (hotel.vervotechPropertyId == null) return null;
  return source.distances[String(hotel.vervotechPropertyId)] ?? null;
}

/** Hotels nearest the venue first; hotels with no stored distance go last, in input order. */
export function hotelsByDistance(
  source: DistanceSource & { hotels: HotelSummary[] }
): HotelSummary[] {
  return source.hotels
    .map((hotel, index) => ({
      hotel,
      index,
      distance: hotelDistance(source, hotel),
    }))
    .sort((a, b) => {
      if (a.distance && b.distance)
        return a.distance.distance_km - b.distance.distance_km;
      if (a.distance) return -1;
      if (b.distance) return 1;
      return a.index - b.index;
    })
    .map((entry) => entry.hotel);
}

function nightsBetween(checkinStr: string, checkoutStr: string): number {
  const [y1, m1, d1] = checkinStr.split("-").map(Number);
  const [y2, m2, d2] = checkoutStr.split("-").map(Number);
  // Date.UTC on calendar components: a pure day count with no timezone involved.
  return Math.round(
    (Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000
  );
}

/** "1 night, Dec 17" or "2 nights from Dec 16", from the calendar-date strings only. */
export function stayShape(hotel: HotelSummary): string | null {
  const checkin = hotel.checkinStr;
  const checkout = hotel.checkoutStr;
  if (!checkin || !checkout) return null;
  const nights = nightsBetween(checkin, checkout);
  if (nights <= 1) return `1 night, ${formatMonthDay(checkin)}`;
  return `${nights} nights from ${formatMonthDay(checkin)}`;
}

/**
 * The UTC offset ("-08:00") an IANA zone has at a local date-time, or null when the
 * zone is unknown. schema.org recommends an offset on Event.startDate; without one
 * consumers may read the naive time in an arbitrary zone.
 */
export function utcOffsetFor(
  isoDate: string,
  localTime: string,
  timeZone: string | null
): string | null {
  if (!timeZone) return null;
  try {
    const [year, month, day] = isoDate.split("-").map(Number);
    const [hour, minute] = localTime.split(":").map(Number);
    // Probe at the naive wall time read as UTC; the zone's offset at that instant is
    // within an hour of the true offset, which is exact except across a DST switch hour.
    const probe = new Date(Date.UTC(year, month - 1, day, hour, minute ?? 0));
    const part = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    })
      .formatToParts(probe)
      .find((p) => p.type === "timeZoneName")?.value;
    if (!part) return null;
    if (part === "GMT") return "+00:00";
    const match = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(part);
    if (!match) return null;
    const hours = match[2].padStart(2, "0");
    const minutes = match[3] ?? "00";
    return `${match[1]}${hours}:${minutes}`;
  } catch {
    return null;
  }
}

/** schema.org startDate: date-time with offset when the time and zone are known, else the date. */
export function schemaStartDate(
  localDate: string,
  localTime: string | null,
  timeZone: string | null
): string {
  if (!localTime) return localDate;
  const offset = utcOffsetFor(localDate, localTime, timeZone);
  return `${localDate}T${localTime}${offset ?? ""}`;
}

export function eventStartDate(snapshot: EventSnapshot): string {
  return schemaStartDate(
    snapshot.local_date,
    snapshot.local_time,
    snapshot.timezone
  );
}

/**
 * Hub events whose page is still published today (the event page expires the morning
 * after the show, so an event dated before today has no page to link to).
 */
export function liveVenueEvents(
  snapshot: VenueSnapshot,
  today: string = todayIsoDate()
): VenueEventRef[] {
  return snapshot.events.filter(
    (event) => !!event.local_date && event.local_date >= today
  );
}

function formatUpdated(generatedAt: string): string {
  return new Date(generatedAt).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function hotelCentsPerPoint(hotel: HotelSummary): number | null {
  return hotel.lowestPointsAmount?.conversionRate ?? null;
}

/** Hotels that carry a cents-per-point value, best value first. */
export function hotelsByValue(snapshot: EventSnapshot): HotelSummary[] {
  return snapshot.hotels
    .filter((hotel) => hotelCentsPerPoint(hotel) != null)
    .sort(
      (a, b) =>
        (hotelCentsPerPoint(b) as number) - (hotelCentsPerPoint(a) as number)
    );
}

export type EventFaqItem = { question: string; answer: string };

/**
 * Single source of truth for the event FAQ. Both the visible FAQ (rendered by the
 * SEO component) and the FAQPage JSON-LD (emitted by the layout) derive from this
 * so their text stays in exact parity — Google drops FAQ rich results whose markup
 * contains questions not visible on the page.
 */
export function buildEventFaq(snapshot: EventSnapshot): EventFaqItem[] {
  const { name, city_name: cityName, hotels } = snapshot;
  const venue = snapshot.venue.name ?? `the venue`;
  const date = formatEventDateShort(snapshot.local_date);
  const topValue = hotelsByValue(snapshot)[0] ?? null;
  const updated = formatUpdated(snapshot.generated_at);

  const items: EventFaqItem[] = [
    {
      question: `Can I book a hotel near ${venue} with points for ${name}?`,
      answer: `Yes. ${hotels.length} premium hotels within ${snapshot.radius_km} km of ${venue} have points availability for the night of ${date}. The list above ranks them by cents-per-point value so you can see where your hotel points stretch furthest on event night.`,
    },
  ];

  const topCpp = topValue ? hotelCentsPerPoint(topValue) : null;
  const topPoints = topValue?.lowestNightlyPoints;
  const topCash = topValue?.bestNightlyRate;
  if (
    topValue &&
    topCpp != null &&
    topPoints != null &&
    topCash?.value != null
  ) {
    items.push({
      question: `Which hotel near ${venue} gives the most points value for ${name}?`,
      answer: `Right now it is ${topValue.propertyName} at about ${topCpp.toFixed(
        2
      )} cents per point, redeeming ${topPoints.toLocaleString("en-US")} ${
        topValue.loyaltyProgram ?? "loyalty"
      } points for a night that would otherwise cost ${formatCurrency(
        topCash.value,
        topCash.currency ?? "USD",
        0
      )}.`,
    });
  }

  const nearest = hotelsByDistance(snapshot).find((hotel) =>
    hotelDistance(snapshot, hotel)
  );
  const nearestDistance = nearest ? hotelDistance(snapshot, nearest) : null;
  const topDistance = topValue ? hotelDistance(snapshot, topValue) : null;
  if (topValue && topDistance) {
    items.push({
      question: `How far is ${topValue.propertyName} from ${venue}?`,
      answer: `${topValue.propertyName} is about ${formatDistance(topDistance)} from ${venue}, measured in a straight line from the venue, so plan on a little longer on foot.`,
    });
  }
  if (nearest && nearestDistance) {
    const walkable = hotelsByDistance(snapshot).filter((hotel) => {
      const d = hotelDistance(snapshot, hotel);
      return d != null && d.walk_minutes <= 20;
    });
    items.push({
      question: `What hotels are within walking distance of ${venue}?`,
      answer:
        walkable.length > 0
          ? `${walkable.length} of the ranked hotels are within a 20-minute walk of ${venue}. The closest is ${nearest.propertyName} at ${formatDistance(nearestDistance)}.`
          : `None of the ranked hotels is within a 20-minute walk of ${venue}; the closest is ${nearest.propertyName} at ${formatDistance(nearestDistance)}.`,
    });
  }

  items.push(
    {
      question: `Do I still earn points and status booking ${cityName} hotels through Gondola?`,
      answer: `Yes. Gondola books directly with the hotel, so you keep your hotel loyalty points and status nights and earn your credit card points. On top of that you get 3-7% back in Gondola Cash, where $1 in Gondola Cash equals $1 toward your next booking.`,
    },
    {
      question: `How often are these ${cityName} event hotel deals updated?`,
      answer: `Gondola refreshes these rankings regularly from live rate data. This page was last updated ${updated}.`,
    }
  );

  return items;
}

function buildHotelListItem(
  hotel: HotelSummary,
  position: number
): Record<string, unknown> {
  const item: Record<string, unknown> = {
    "@type": "Hotel",
    name: hotel.propertyName,
  };
  if (hotel.brandName) item.brand = { "@type": "Brand", name: hotel.brandName };
  if (hotel.stars != null)
    item.starRating = { "@type": "Rating", ratingValue: hotel.stars };
  if (hotel.cityName || hotel.countryCode) {
    const address: Record<string, unknown> = { "@type": "PostalAddress" };
    if (hotel.cityName) address.addressLocality = hotel.cityName;
    if (hotel.countryCode) address.addressCountry = hotel.countryCode;
    item.address = address;
  }
  if (hotel.averageGuestRating != null && hotel.reviewCount != null) {
    item.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: hotel.averageGuestRating,
      reviewCount: hotel.reviewCount,
    };
  }
  const detailParts: string[] = [];
  if (hotel.lowestNightlyPoints != null) {
    detailParts.push(
      `${hotel.lowestNightlyPoints.toLocaleString("en-US")} ${
        hotel.loyaltyProgram ?? "loyalty"
      } points per night`
    );
  }
  if (hotel.bestNightlyRate?.value != null) {
    detailParts.push(
      `${formatCurrency(
        hotel.bestNightlyRate.value,
        hotel.bestNightlyRate.currency ?? "USD",
        0
      )} per night in cash`
    );
  }
  const cpp = hotelCentsPerPoint(hotel);
  if (cpp != null) detailParts.push(`about ${cpp.toFixed(2)} cents per point`);
  if (detailParts.length > 0) item.description = `${detailParts.join(", ")}.`;
  return { "@type": "ListItem", position, item };
}

export function eventPageTitle(snapshot: EventSnapshot): string {
  return `Hotels Near ${snapshot.venue.name ?? snapshot.city_name} for ${snapshot.name} (${formatEventDateShort(
    snapshot.local_date
  )}) - Book With Points or Cash`;
}

export function eventPageDescription(snapshot: EventSnapshot): string {
  return `Compare points and cash rates at ${snapshot.hotels.length} hotels near ${
    snapshot.venue.name ?? snapshot.city_name
  } for ${snapshot.name} on ${formatEventDateShort(
    snapshot.local_date
  )}. Ranked by cents-per-point value across Marriott, Hilton, Hyatt, IHG, and more.`;
}

/**
 * All JSON-LD blocks for an event page from one snapshot: the Event itself, the
 * ranked ItemList of hotels, and a FAQPage built from `buildEventFaq` so the
 * markup matches the visible FAQ exactly.
 */
const PERFORMER_TYPES: Record<PerformerKind, string> = {
  person: "Person",
  group: "PerformingGroup",
  team: "SportsTeam",
  unknown: "PerformingGroup",
};

const PERFORMER_KINDS: readonly PerformerKind[] = [
  "person",
  "group",
  "team",
  "unknown",
];

/** Validate one stored performer entry; anything off-shape is dropped by the loader. */
export function performerFrom(value: unknown): EventPerformer | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.name !== "string" || !raw.name.trim()) return null;
  const kind = PERFORMER_KINDS.includes(raw.kind as PerformerKind)
    ? (raw.kind as PerformerKind)
    : "unknown";
  return { name: raw.name, kind };
}

/** Ticketmaster status code -> schema.org eventStatus. Rescheduled stays Scheduled: Google
 *  requires `previousStartDate` for EventRescheduled and the feed does not give it. */
export function eventStatusUrl(status: string | null): string {
  const code = (status ?? "").trim().toLowerCase();
  if (code === "cancelled" || code === "canceled")
    return "https://schema.org/EventCancelled";
  if (code === "postponed") return "https://schema.org/EventPostponed";
  return "https://schema.org/EventScheduled";
}

/**
 * Google's guidance: InStock while tickets are on sale; when they are not on sale yet,
 * omit availability and rely on validFrom. Ticketmaster's `offsale` also covers "window
 * not open yet" and "sales closed", so it never becomes SoldOut.
 */
function offerAvailability(
  status: string | null,
  saleStart: string | null,
  now: Date
): string | undefined {
  if (saleStart) {
    const opens = new Date(saleStart);
    if (!Number.isNaN(opens.getTime()) && opens.getTime() > now.getTime()) {
      return undefined;
    }
  }
  return (status ?? "").trim().toLowerCase() === "onsale"
    ? "https://schema.org/InStock"
    : undefined;
}

export type EventJsonLdInput = EventFacts & {
  name: string;
  url: string;
  local_date: string;
  local_time: string | null;
  timezone: string | null;
  price_range: EventPriceRange | null;
  venueName: string | null;
  cityName: string;
  /** Reference time for the PreOrder decision; defaults to now. */
  now?: Date;
};

/**
 * The Event fields Google recommends, built once for the night page's own block and for
 * the hub's nested `Place.event[]` so the two never disagree. Nothing is invented: price,
 * validFrom, performer and organizer appear only when the feed supplied them, and endDate
 * falls back to the event's own date (date-only) when Ticketmaster gives no end.
 */
export function eventJsonLd(input: EventJsonLdInput): Record<string, unknown> {
  const venue = input.venueName ?? input.cityName;
  const time = formatEventTime(input.local_time);
  const price = ticketPriceText(input.price_range);
  const description = `${input.name} at ${venue} in ${input.cityName} on ${formatEventDate(input.local_date)}${time ? ` at ${time}` : ""}.${price ? ` ${price}.` : ""} Compare points and cash rates at hotels within walking distance.`;
  const startDate = schemaStartDate(
    input.local_date,
    input.local_time,
    input.timezone
  );
  // Without a feed end, the event ends when it starts as far as we can say; a date-only
  // endDate would sort before a timed startDate.
  const explicitEnd =
    input.end_local_date &&
    !(input.end_local_date === input.local_date && !input.end_local_time)
      ? schemaStartDate(
          input.end_local_date,
          input.end_local_time,
          input.timezone
        )
      : null;
  const endDate = explicitEnd ?? startDate;
  const offers: Record<string, unknown> = { "@type": "Offer", url: input.url };
  const minimum = input.price_range?.minimum;
  if (typeof minimum === "number" && Number.isFinite(minimum)) {
    offers.price = minimum;
    offers.priceCurrency = input.price_range?.currency ?? "USD";
  }
  const availability = offerAvailability(
    input.status,
    input.sale_start,
    input.now ?? new Date()
  );
  if (availability) offers.availability = availability;
  if (input.sale_start) offers.validFrom = input.sale_start;

  const event: Record<string, unknown> = {
    "@type": "Event",
    name: input.name,
    description,
    startDate,
    endDate,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: eventStatusUrl(input.status),
    url: input.url,
    offers,
  };
  if (input.performers.length > 0) {
    event.performer = input.performers.map((p) => ({
      "@type": PERFORMER_TYPES[p.kind],
      name: p.name,
    }));
  }
  if (input.organizer) {
    event.organizer = { "@type": "Organization", name: input.organizer };
  }
  return event;
}

export function buildEventJsonLd(
  snapshot: EventSnapshot,
  pageUrl: string
): {
  event: Record<string, unknown>;
  itemList: Record<string, unknown>;
  faqPage: Record<string, unknown>;
} {
  const venue = snapshot.venue;
  const address: Record<string, unknown> = { "@type": "PostalAddress" };
  if (venue.address_line_1) address.streetAddress = venue.address_line_1;
  if (venue.city) address.addressLocality = venue.city;
  if (venue.state_code) address.addressRegion = venue.state_code;
  if (venue.postal_code) address.postalCode = venue.postal_code;
  if (venue.country_code) address.addressCountry = venue.country_code;

  const location: Record<string, unknown> = {
    "@type": "Place",
    name: venue.name ?? snapshot.city_name,
    address,
  };
  if (venue.latitude != null && venue.longitude != null) {
    location.geo = {
      "@type": "GeoCoordinates",
      latitude: venue.latitude,
      longitude: venue.longitude,
    };
  }

  const event: Record<string, unknown> = {
    "@context": "https://schema.org",
    ...eventJsonLd({
      ...snapshot,
      venueName: venue.name,
      cityName: snapshot.city_name,
    }),
    location,
  };
  if (snapshot.image_url) event.image = snapshot.image_url;

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Hotels near ${venue.name ?? snapshot.city_name} for ${snapshot.name}`,
    description: eventPageDescription(snapshot),
    url: pageUrl,
    provider: { "@id": "https://www.gondola.ai/#organization" },
    numberOfItems: snapshot.hotels.length,
    itemListElement: snapshot.hotels.map((hotel, index) =>
      buildHotelListItem(hotel, index + 1)
    ),
  };

  const faqPage = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: buildEventFaq(snapshot).map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return { event, itemList, faqPage };
}

// ---------------------------------------------------------------------------
// Venue hubs
// ---------------------------------------------------------------------------

export function venuePageTitle(snapshot: VenueSnapshot): string {
  const venue = snapshot.venue.name ?? snapshot.city_name;
  return `Hotels Near ${venue}, ${snapshot.city_name} - Book With Points or Cash`;
}

export function venuePageDescription(snapshot: VenueSnapshot): string {
  const venue = snapshot.venue.name ?? snapshot.city_name;
  return `${snapshot.hotels.length} premium hotels near ${venue} with walking distance, cash rates, and points rates side by side, plus upcoming events at the venue. Ranked by cents-per-point value across Marriott, Hilton, Hyatt, IHG, and more.`;
}

/** Single source of truth for the venue hub FAQ (visible FAQ and FAQPage JSON-LD). */
export function buildVenueFaq(snapshot: VenueSnapshot): EventFaqItem[] {
  const venue = snapshot.venue.name ?? snapshot.city_name;
  const cityName = snapshot.city_name;
  const byValue = snapshot.hotels
    .filter((hotel) => hotelCentsPerPoint(hotel) != null)
    .sort(
      (a, b) =>
        (hotelCentsPerPoint(b) as number) - (hotelCentsPerPoint(a) as number)
    );
  const topValue = byValue[0] ?? null;
  const nearest = hotelsByDistance(snapshot).find((hotel) =>
    hotelDistance(snapshot, hotel)
  );
  const nearestDistance = nearest ? hotelDistance(snapshot, nearest) : null;
  const walkable = hotelsByDistance(snapshot).filter((hotel) => {
    const d = hotelDistance(snapshot, hotel);
    return d != null && d.walk_minutes <= 20;
  });
  const updated = formatUpdated(snapshot.generated_at);

  const items: EventFaqItem[] = [
    {
      question: `What hotels are within walking distance of ${venue}?`,
      answer:
        nearest && nearestDistance
          ? `${walkable.length} of the ${snapshot.hotels.length} ranked hotels are within a 20-minute walk of ${venue}. The closest is ${nearest.propertyName} at ${formatDistance(nearestDistance)}.`
          : `Gondola ranks ${snapshot.hotels.length} premium hotels within ${snapshot.radius_km} km of ${venue}.`,
    },
  ];

  const topCpp = topValue ? hotelCentsPerPoint(topValue) : null;
  if (
    topValue &&
    topCpp != null &&
    topValue.lowestNightlyPoints != null &&
    topValue.bestNightlyRate?.value != null
  ) {
    items.push({
      question: `Which hotel near ${venue} gives the most points value?`,
      answer: `Right now it is ${topValue.propertyName} at about ${topCpp.toFixed(2)} cents per point, redeeming ${topValue.lowestNightlyPoints.toLocaleString("en-US")} ${topValue.loyaltyProgram ?? "loyalty"} points for a night that would otherwise cost ${formatCurrency(topValue.bestNightlyRate.value, topValue.bestNightlyRate.currency ?? "USD", 0)}.`,
    });
  }

  const programs = Array.from(
    new Set(
      snapshot.hotels.map((hotel) => hotel.loyaltyProgram).filter(Boolean)
    )
  ) as string[];
  if (programs.length > 0) {
    items.push({
      question: `Can I use hotel points near ${venue}?`,
      answer: `Yes. Hotels near ${venue} take ${programs.slice(0, 4).join(", ")}${programs.length > 4 ? " and more" : ""} points, and Gondola shows the points price next to the cash rate so you can see which way costs less for your dates.`,
    });
  }

  const upcoming = liveVenueEvents(snapshot);
  if (upcoming.length > 0) {
    const next = upcoming[0];
    items.push({
      question: `What events are coming up at ${venue}?`,
      answer: `${upcoming.length} upcoming ${upcoming.length === 1 ? "event is" : "events are"} listed, starting with ${next.name}${next.local_date ? ` on ${formatEventDateShort(next.local_date)}` : ""}. Each event has its own page with hotel rates for that night.`,
    });
  }

  items.push(
    {
      question: `Do I still earn points and status booking ${cityName} hotels through Gondola?`,
      answer: `Yes. Gondola books directly with the hotel, so you keep your hotel loyalty points and status nights and earn your credit card points. On top of that you get 3-7% back in Gondola Cash, where $1 in Gondola Cash equals $1 toward your next booking.`,
    },
    {
      question: `How often are these ${venue} hotel rankings updated?`,
      answer: `Gondola refreshes these rankings regularly from live rate data. This page was last updated ${updated}.`,
    }
  );
  return items;
}

/** Place, ItemList, and FAQPage JSON-LD for a venue hub from one snapshot. */
export function buildVenueJsonLd(
  snapshot: VenueSnapshot,
  pageUrl: string,
  today: string = todayIsoDate()
): {
  place: Record<string, unknown>;
  itemList: Record<string, unknown>;
  faqPage: Record<string, unknown>;
} {
  const venue = snapshot.venue;
  const address: Record<string, unknown> = { "@type": "PostalAddress" };
  if (venue.address_line_1) address.streetAddress = venue.address_line_1;
  if (venue.city) address.addressLocality = venue.city;
  if (venue.state_code) address.addressRegion = venue.state_code;
  if (venue.postal_code) address.postalCode = venue.postal_code;
  if (venue.country_code) address.addressCountry = venue.country_code;

  const place: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Place",
    name: venue.name ?? snapshot.city_name,
    address,
    url: pageUrl,
  };
  if (venue.latitude != null && venue.longitude != null) {
    place.geo = {
      "@type": "GeoCoordinates",
      latitude: venue.latitude,
      longitude: venue.longitude,
    };
  }
  if (snapshot.image) place.image = snapshot.image.url;
  const liveEvents = liveVenueEvents(snapshot, today);
  if (liveEvents.length > 0) {
    place.event = liveEvents.map((event) =>
      eventJsonLd({
        ...event,
        // `liveVenueEvents` only returns dated events, so `local_date` is set here.
        local_date: event.local_date ?? "",
        timezone: event.timezone ?? null,
        venueName: venue.name,
        cityName: snapshot.city_name,
      })
    );
  }

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Hotels near ${venue.name ?? snapshot.city_name}`,
    description: venuePageDescription(snapshot),
    url: pageUrl,
    provider: { "@id": "https://www.gondola.ai/#organization" },
    numberOfItems: snapshot.hotels.length,
    itemListElement: snapshot.hotels.map((hotel, index) =>
      buildHotelListItem(hotel, index + 1)
    ),
  };

  const faqPage = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: buildVenueFaq(snapshot).map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return { place, itemList, faqPage };
}
